// 临时定义枚举，避免依赖问题
enum EMAIL_VERIFICATION_ENUM {
  VALID = 'valid',
  NOT_VERIFIED = 'not_verified',
  RISKY = 'risky',
  VERIFING = 'verifying',
  FAILED = 'failed',
  UNKNOWN = 'unknown',
}

// Mock 接口类型定义
export interface CommonUploadReq {
  file: File;
  relatedId: string;
  type: 'MAIL_PICS' | 'MAIL_ATTACHMENT' | 'SIGNATURE_PICS' | 'SIGNATURE';
}

const map: { [key: string]: EMAIL_VERIFICATION_ENUM } = {
  valid: EMAIL_VERIFICATION_ENUM.VALID,
  'not verified': EMAIL_VERIFICATION_ENUM.NOT_VERIFIED,
  not_verify: EMAIL_VERIFICATION_ENUM.NOT_VERIFIED,
  risky: EMAIL_VERIFICATION_ENUM.RISKY,
  '0': EMAIL_VERIFICATION_ENUM.NOT_VERIFIED,
  '1': EMAIL_VERIFICATION_ENUM.VERIFING,
  '2': EMAIL_VERIFICATION_ENUM.VALID,
  '3': EMAIL_VERIFICATION_ENUM.RISKY,
  '4': EMAIL_VERIFICATION_ENUM.FAILED,
  '5': EMAIL_VERIFICATION_ENUM.UNKNOWN,
  unknown: EMAIL_VERIFICATION_ENUM.UNKNOWN,
  processing: EMAIL_VERIFICATION_ENUM.VERIFING,
  quota: EMAIL_VERIFICATION_ENUM.UNKNOWN,
  failed: EMAIL_VERIFICATION_ENUM.FAILED,
};

const default_status = EMAIL_VERIFICATION_ENUM.NOT_VERIFIED;

export function transformEmailCheckStatus(strVal: string): EMAIL_VERIFICATION_ENUM {
  if (!strVal || !strVal.length) return default_status;
  return map[strVal.toLowerCase()] || default_status;
}

/**
 * 邮件内容DOM格式过滤器
 * 处理邮件标准协议兼容性问题
 */
export function filterEmailContentDom(emailContent: string): string {
  let filteredContent = emailContent;

  // 1. 修复 white-space: pre 为 white-space: pre-wrap
  filteredContent = filteredContent.replace(/white-space\s*:\s*pre(?!-wrap)(?=\s*[;\s}]|$)/gi, 'white-space: pre-wrap');

  // 2. 处理 <pre> 标签，确保添加 white-space: pre-wrap 样式
  filteredContent = filteredContent.replace(/<pre(\s+[^>]*)?>/gi, (match, attributes) => {
    if (!attributes) {
      // 没有任何属性，直接添加 style
      return '<pre style="white-space: pre-wrap;">';
    } else if (!/style\s*=/i.test(attributes)) {
      // 有其他属性但没有 style 属性
      return `<pre${attributes} style="white-space: pre-wrap;">`;
    } else {
      // 已有 style 属性，检查是否包含 white-space
      return match.replace(/style\s*=\s*["']([^"']*)["']/i, (styleMatch, styleContent) => {
        const trimmedStyle = styleContent.trim();
        if (!/white-space\s*:/i.test(trimmedStyle)) {
          // style 中没有 white-space，添加它
          const separator = trimmedStyle && !trimmedStyle.endsWith(';') ? '; ' : '';
          return `style="${trimmedStyle}${separator}white-space: pre-wrap;"`;
        }
        // 已经有 white-space，保持原样
        return styleMatch;
      });
    }
  });

  // 3. 移除可能影响邮件客户端渲染的CSS属性
  const problematicStyles = [
    /position\s*:\s*(fixed|absolute)\s*;?/gi,
    /transform\s*:\s*[^;]+\s*;?/gi,
    /transition\s*:\s*[^;]+\s*;?/gi,
    /animation\s*:\s*[^;]+\s*;?/gi,
    /box-shadow\s*:\s*[^;]+\s*;?/gi,
  ];

  problematicStyles.forEach(regex => {
    filteredContent = filteredContent.replace(regex, '');
  });

  // 4. 修复可能的CSS语法问题
  filteredContent = filteredContent.replace(/;\s*;+/g, ';');
  filteredContent = filteredContent.replace(/style\s*=\s*["'][;\s]*["']/gi, '');

  // 5. 确保表格相关样式的兼容性
  filteredContent = filteredContent.replace(
    /<table(?![^>]*border\s*=)/gi,
    '<table border="0" cellpadding="0" cellspacing="0"'
  );

  // 6. 处理图片的显示问题
  filteredContent = filteredContent.replace(/<img(?![^>]*style\s*=\s*["'][^"']*display\s*:)/gi, match => {
    if (match.includes('style=')) {
      return match.replace(/style\s*=\s*["']([^"']*)["']/, (_, styleContent) => {
        const newStyle = styleContent.trim();
        return `style="${newStyle}${newStyle && !newStyle.endsWith(';') ? ';' : ''} display: block;"`;
      });
    } else {
      return match.replace('<img', '<img style="display: block;"');
    }
  });

  return filteredContent;
}

/**
 * Mock 文件上传接口
 * 模拟文件上传过程，返回一个模拟的 URL
 */
export async function commonUpload(params: CommonUploadReq): Promise<string> {
  const { file, relatedId, type } = params;

  // 模拟上传延迟
  await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

  // 模拟上传失败的情况（10% 概率）
  if (Math.random() < 0.1) {
    throw new Error('Upload failed');
  }

  // 根据文件类型生成不同的模拟 URL
  const baseUrl = 'https://mock-storage.example.com';
  const fileExtension = file.name.split('.').pop() || 'unknown';
  const timestamp = Date.now();

  if (type === 'MAIL_PICS') {
    return `${baseUrl}/images/${relatedId}_${timestamp}.${fileExtension}`;
  } else {
    return `${baseUrl}/attachments/${relatedId}_${timestamp}.${fileExtension}`;
  }
}

/**
 * Mock 图片重新加载接口
 * 模拟图片 URL 刷新功能
 */
export async function reloadImg(urls: string[]): Promise<{ [key: string]: string }> {
  // 模拟网络延迟
  await new Promise(resolve => setTimeout(resolve, 500));

  const result: { [key: string]: string } = {};

  urls.forEach(url => {
    // 模拟重新生成 URL（添加时间戳参数）
    const separator = url.includes('?') ? '&' : '?';
    result[url] = `${url}${separator}t=${Date.now()}`;
  });

  return result;
}

/**
 * 过滤掉图片上传失败时的错误处理元素（Retry、Delete按钮等）
 * @param content - 编辑器内容HTML
 * @returns 过滤后的内容
 */
export function filterUploadErrorElements(content: string): string {
  if (!content) return content;

  // 创建临时DOM容器来解析和操作HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  // 首先移除所有包含上传失败图片的 upload-img-wrapper（包含所有错误处理元素）
  const errorWrappers = tempDiv.querySelectorAll('.upload-img-wrapper');
  errorWrappers.forEach(wrapper => {
    const hasErrorImg = wrapper.querySelector('img[src="uploaderror"]');
    const hasUploadingImg = wrapper.querySelector('img[upload-id]');
    if (hasErrorImg || hasUploadingImg) {
      // 移除整个包装器（包含图片、错误提示、按钮等）
      wrapper.remove();
    }
  });

  // 移除所有独立的错误处理元素（防止有遗漏的情况）
  const errorElements = [
    '.btn-wrapper', // 包含 Retry 和 Delete 按钮的包装器
    '.paste-img-restart', // Retry 按钮
    '.paste-img-del', // Delete 按钮
    '.paste-img-reload', // 重载容器（包含错误信息）
    '.paste-img-load', // 加载提示
    '.paste-img-title', // 错误标题
    '.holder', // 占位符
  ];

  errorElements.forEach(selector => {
    const elements = tempDiv.querySelectorAll(selector);
    elements.forEach(el => el.remove());
  });

  // 移除所有独立的错误图片（不在 wrapper 中的）
  const standaloneErrorImgs = tempDiv.querySelectorAll('img[src="uploaderror"], img[upload-id]');
  standaloneErrorImgs.forEach(img => img.remove());

  return tempDiv.innerHTML;
}

/**
 * 邮件附件类型定义（来自详情接口）
 */
export interface EmailAttachmentFromAPI {
  downloadUrl: string;
  expireTime: number;
  fileName: string;
  fileSize: number;
  type: number;
  nosKey?: string;
}

/**
 * 邮件编辑器附件类型定义
 */
export interface MailEditorAttachment {
  nosKey: string;
  fileName: string;
  fileSize: number;
  type: string;
  downloadUrl: string;
  expireTime?: number;
  identity?: string;
  status?: 'waiting' | 'uploading' | 'success' | 'failed';
  originFile?: File;
}

/**
 * 转换邮件附件为编辑器附件格式
 * 用于读信转写信时的附件类型转换
 *
 * @param apiAttachments - 来自邮件详情接口的附件列表
 * @returns 编辑器可用的附件列表
 */
export function transformEmailAttachmentsToEditorFormat(
  apiAttachments: EmailAttachmentFromAPI[]
): MailEditorAttachment[] {
  if (!apiAttachments || apiAttachments.length === 0) {
    return [];
  }

  return apiAttachments.map((att, index) => ({
    nosKey: att.nosKey || '',
    fileName: att.fileName,
    fileSize: att.fileSize,
    // 附件类型转换：0 普通附件 -> 'MAIL_ATTACHMENT', 1 内联附件 -> 'MAIL_PICS'
    type: att.type === 1 ? 'MAIL_PICS' : 'MAIL_ATTACHMENT',
    downloadUrl: att.downloadUrl,
    expireTime: att.expireTime,
    identity: `${att.fileName}_${index}_${Date.now()}`,
    status: 'success' as const,
  }));
}
